import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import FeeSchedulesQuery from "work/fee-schedules/_api/queries/contracts/fee-schedules-query";
import FeeSchedulesFilterSchema from "work/fee-schedules/_api/queries/contracts/filtering/_schema/fee-schedules-filter-schema";
import FeeSchedulesFilterParameters from "work/fee-schedules/_api/queries/contracts/filtering/fee-schedules-filter-parameters";
import FeeSchedulesOrderSchema from "work/fee-schedules/_api/queries/contracts/ordering/_schema/fee-schedules-order-schema";
import FeeSchedulesOrderParameters, {
	FeeSchedulesOrderField,
} from "work/fee-schedules/_api/queries/contracts/ordering/fee-schedules-order-parameters";
import z from "zod";

export type FeeSchedulesQueryJSON = z.infer<typeof FeeSchedulesQuerySchema.FeeSchedulesQueryJSONZ>;
export type FeeSchedulesQueryParams = z.infer<typeof FeeSchedulesQuerySchema.FeeSchedulesQueryZ>;

export default class FeeSchedulesQuerySchema {
	public static readonly FeeSchedulesQueryJSONZ = z.object({
		filter: FeeSchedulesFilterSchema.FeeSchedulesFilterJSONZ,
		order: FeeSchedulesOrderSchema.FeeSchedulesOrderJSONZ,
		pagination: PaginationSchema.PaginationZ,
	});

	public static readonly FeeSchedulesQueryZ = z.object({
		filter: FeeSchedulesFilterSchema.FeeSchedulesFilterZ,
		order: FeeSchedulesOrderSchema.FeeSchedulesOrderZ,
		pagination: PaginationSchema.PaginationZ,
	});

	public static validateJSON(values: unknown): FeeSchedulesQueryJSON {
		return parseOrThrow(FeeSchedulesQuerySchema.FeeSchedulesQueryJSONZ, values, {
			logError: true,
			schemaName: "FeeSchedulesQuerySchema",
			operation: "validateJSON",
			payloadSample: values,
		});
	}

	public static isValidJSON(values: unknown): values is FeeSchedulesQueryJSON {
		try {
			FeeSchedulesQuerySchema.validateJSON(values);
			return true;
		} catch {
			return false;
		}
	}

	public static deserialize(json: FeeSchedulesQueryJSON): FeeSchedulesQuery {
		const parsed = FeeSchedulesQuerySchema.validateJSON(json);
		return FeeSchedulesQuerySchema.codec.decode(parsed);
	}

	public static serialize(query: FeeSchedulesQuery): FeeSchedulesQueryJSON {
		const { filterParameters, orderParameters, paginationParameters } = query;

		const params: FeeSchedulesQueryParams = {
			filter: filterParameters,
			order: orderParameters
				? {
						orderBy: orderParameters.orderBy,
						orderDir: orderParameters.orderDir,
					}
				: undefined,
			pagination: {
				pageIndex: paginationParameters.pageIndex,
				pageSize: paginationParameters.pageSize,
			},
		};

		parseOrThrow(FeeSchedulesQuerySchema.FeeSchedulesQueryZ, params, {
			logError: true,
			schemaName: "FeeSchedulesQuerySchema",
			operation: "serialize",
			payloadSample: params,
		});

		return FeeSchedulesQuerySchema.codec.encode(query);
	}

	private static readonly codec = z.codec(
		FeeSchedulesQuerySchema.FeeSchedulesQueryJSONZ,
		z.custom<FeeSchedulesQuery>((value) => value instanceof FeeSchedulesQuery),
		{
			decode: (json): FeeSchedulesQuery => {
				const filterParams = FeeSchedulesFilterSchema.deserialize(json.filter);
				const orderParams = FeeSchedulesOrderSchema.deserialize(json.order);
				const paginationParams = PaginationSchema.deserialize(json.pagination);

				const orderParametersData = {
					orderBy: orderParams?.orderBy ?? FeeSchedulesOrderField.CreatedDate,
					orderDir: orderParams?.orderDir ?? OrderDirection.Descending,
				};

				return new FeeSchedulesQuery({
					filterParameters: new FeeSchedulesFilterParameters(filterParams),
					orderParameters: new FeeSchedulesOrderParameters(orderParametersData),
					paginationParameters: PaginationParameters.create(paginationParams),
				});
			},
			encode: (query): FeeSchedulesQueryJSON => ({
				filter: FeeSchedulesFilterSchema.serialize(query.filterParameters),
				order: FeeSchedulesOrderSchema.serialize({
					orderBy: query.orderParameters.orderBy,
					orderDir: query.orderParameters.orderDir,
				}),
				pagination: PaginationSchema.serialize({
					pageIndex: query.paginationParameters.pageIndex,
					pageSize: query.paginationParameters.pageSize,
				}),
			}),
		}
	);
}
